import React, {useState, useMemo} from 'react'
import {
    Flex,
    useMultiStyleConfig,
    Button,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Text,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getGlobalLink} from '../../utils/contentstack-utils'
import PropTypes from 'prop-types'
import PopoverThirdLevelCategories from './popover-third-level-categories'
import PopoverContentImage from './popover-content-image'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Link as RouterLink} from 'react-router-dom'
import {Link as ChakraLink} from '@chakra-ui/react'

const DrawerSecondLevelContent = ({
    first_level_image_section = {},
    second_level_categories = [],
    secondLevelTitle,
}) => {
    const categoryData = useGetCategoriesData()
    const styles = useMultiStyleConfig('BraunMenu', {})
    const popoverContent = styles?.popoverContent || {}
    const drawerMenu = styles?.drawerMenu || {}
    const [hoveredCategory, setHoveredCategory] = useState(null)

    const {locale} = useMultiSite()

    const third_level_categories = useMemo(
        () =>
            second_level_categories.map((subCat) => ({
                uid: subCat._metadata.uid,
                section: subCat.third_level_categories,
            })),
        [second_level_categories],
    )

    const selectedCategory = useMemo(() => {
        if (!hoveredCategory) return null
        return second_level_categories.find((cat) => cat._metadata?.uid === hoveredCategory) || null
    }, [hoveredCategory, second_level_categories])

    return (
        <Flex {...drawerMenu?.contentContainer}>
            <Flex flexDir="column" gap="medium.base">
                {secondLevelTitle && (
                    <Text {...popoverContent?.categoriesHeader}>{secondLevelTitle}</Text>
                )}
                <Flex {...drawerMenu?.contentContainerHalf}>
                    <Flex w="100%" h="auto">
                        <Accordion>
                            {second_level_categories.length > 0 &&
                                second_level_categories.map((category) => {
                                    const hasThirdLevel =
                                        category?.third_level_categories?.length > 0
                                    const {url, label, isAbsolute} =
                                        getGlobalLink({
                                            link: category?.second_level_category,
                                            categoriesData: categoryData,
                                            locale: locale?.alias,
                                        }) || {}
                                    return (
                                        <AccordionItem
                                            key={category?._metadata?.uid}
                                            border="unset"
                                            padding="unset"
                                            paddingBottom="xsmall.base"
                                        >
                                            <AccordionButton>
                                                {!hasThirdLevel ? (
                                                    <Button
                                                        as={isAbsolute ? ChakraLink : RouterLink}
                                                        target={isAbsolute ? '_blank' : undefined}
                                                        rel={
                                                            isAbsolute
                                                                ? 'noopener noreferrer'
                                                                : undefined
                                                        }
                                                        textDecoration="none"
                                                        _hover={{textDecoration: 'none'}}
                                                        {...popoverContent?.categoriesTitle}
                                                        color={
                                                            category?._metadata?.uid ===
                                                            hoveredCategory
                                                                ? 'text.tertiary'
                                                                : 'text.default'
                                                        }
                                                        onMouseEnter={() =>
                                                            setHoveredCategory(
                                                                category?._metadata?.uid,
                                                            )
                                                        }
                                                        href={isAbsolute ? url : ''}
                                                        to={!isAbsolute ? url : ''}
                                                    >
                                                        {label}
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        {...popoverContent?.categoriesTitle}
                                                        color={
                                                            category?._metadata?.uid ===
                                                            hoveredCategory
                                                                ? 'text.tertiary'
                                                                : 'text.default'
                                                        }
                                                        onMouseEnter={() =>
                                                            setHoveredCategory(
                                                                category?._metadata?.uid,
                                                            )
                                                        }
                                                    >
                                                        {label}
                                                    </Button>
                                                )}
                                            </AccordionButton>
                                            {hasThirdLevel && (
                                                <AccordionPanel>
                                                    <PopoverThirdLevelCategories
                                                        third_level_categories={
                                                            third_level_categories.find(
                                                                (item) =>
                                                                    item.uid === hoveredCategory,
                                                            )?.section || []
                                                        }
                                                        isDrawer={true}
                                                    />
                                                </AccordionPanel>
                                            )}
                                        </AccordionItem>
                                    )
                                })}
                        </Accordion>
                    </Flex>
                </Flex>
            </Flex>
            {/* Right side: Image section */}
            <Flex {...drawerMenu?.contentContainerHalf}>
                <PopoverContentImage
                    first_level_image_section={first_level_image_section}
                    selected_second_level_category={selectedCategory}
                    categoryData={categoryData}
                    isDrawer={true}
                />
            </Flex>
        </Flex>
    )
}

DrawerSecondLevelContent.propTypes = {
    first_level_image_section: PropTypes.object,
    second_level_categories: PropTypes.array,
    secondLevelTitle: PropTypes.string,
}

export default DrawerSecondLevelContent
